/**
 * Landing page.
 *
 * Signed in: a greeting with the day's quick actions, the headline numbers
 * (projects, explorations, selections, reproductions), then the team's five
 * most recent items and the projects. Signed out: the greeting and a sign-in
 * prompt.
 *
 * Everything shown comes from the list endpoints the overview pages use;
 * a failing request leaves its block empty rather than the page broken.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Paper, Stack, Tooltip, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LoginIcon from "@mui/icons-material/Login";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import HistoryIcon from "@mui/icons-material/History";
import CreateNewFolderOutlinedIcon from "@mui/icons-material/CreateNewFolderOutlined";
import axios from "axios";
import { fetchModuleAccess } from "../components/ModuleRoute";
import { ITEM_KIND_STYLES } from "../components/itemKindStyles";
import { PROJECT_TILE_COLOR, countsOf, projectTile } from "../components/ProjectList";
import { explorationStatusPresentation, normalizeExplorationStatus } from "../components/ExplorationCard";
import { reproductionKindOf } from "../components/ReproductionCard";
import { fetchProjects, PROJECTS_ROUTE, projectRoute } from "../helper/projects";
import { EXPLORATION_LIST_ROUTE, EXPLORATION_SETUP_ROUTE, explorationResumeRoute } from "../helper/explorationRoutes";
import {
  SELECTIONS_BASE, SELECTION_TYPE_CHOICE_ROUTE, selectionKind, selectionResumeRoute, selectionStatusPresentation,
} from "../helper/selectionFlow";
import { REPRODUCTIONS_BASE, REPRODUCTION_TYPE_CHOICE_ROUTE, reproductionRoute } from "../helper/reproductionFlow";
import { MODULE_COLOR_EXPLORER, MODULE_IMAGE_REPRODUCTION, MODULE_SELECTION_EXPLORER } from "../helper/moduleIds";
import { formatDateOnly, formatDateTime } from "../helper/formatDate";
import { displayName } from "../helper/displayName";

const RECENT_PER_KIND = 5;
const RECENT_SHOWN = 5;
const PROJECTS_SHOWN = 5;

/** The same navy as the side menu, so the hero and the panel headers read
 *  as one chrome with it. */
const NAVY_GRADIENT = "linear-gradient(180deg, #1a2230 0%, #202b3d 100%)";

/** The "New …" quick actions of the hero, one per module. */
const QUICK_ACTIONS = [
  { kind: "exploration", moduleId: MODULE_COLOR_EXPLORER, to: EXPLORATION_SETUP_ROUTE, label: "New exploration" },
  { kind: "selection", moduleId: MODULE_SELECTION_EXPLORER, to: SELECTION_TYPE_CHOICE_ROUTE, label: "New selection" },
  { kind: "reproduction", moduleId: MODULE_IMAGE_REPRODUCTION, to: REPRODUCTION_TYPE_CHOICE_ROUTE, label: "New reproduction" },
];

function greetingFor(date) {
  const hour = date.getHours();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

const isSignedIn = () => (localStorage.getItem("token") || "").length > 0;

/** One recent item of any kind, with what the activity list shows and opens. */
function recentOf(kind, row) {
  if (kind === "exploration") {
    const status = normalizeExplorationStatus(row.status);
    const presentation = explorationStatusPresentation(status);
    return {
      key: `exploration:${row.id}`, kind, name: row.name, type: null,
      status: presentation.label, statusColor: presentation.color, project: row.project_name || null,
      date: row.modified_at || row.created_at, to: explorationResumeRoute(row.id, status),
    };
  }
  if (kind === "selection") {
    const presentation = selectionStatusPresentation(row.status);
    return {
      key: `selection:${row.id}`, kind, name: row.name, type: selectionKind(row.selection_type).navLabel,
      status: presentation.label, statusColor: presentation.color, project: row.project_name || null,
      date: row.modified_at || row.created_at,
      to: selectionResumeRoute(row.id, row.status, row.selection_type),
    };
  }
  const descriptor = reproductionKindOf(row);
  return {
    key: `reproduction:${row.id}`, kind, name: row.name, type: descriptor.navLabel,
    status: row.setup_final ? "Marking" : "Set-up", statusColor: row.setup_final ? "#16a34a" : "#4C7EFF",
    project: row.project_name || null, date: row.modified_at || row.created_at,
    to: reproductionRoute(descriptor.type, row.id, row.setup_final ? "marking" : "setup"),
  };
}

/** "today", "yesterday", "3 d ago", else the calendar date. */
function relativeDate(value, now = new Date()) {
  const parsed = new Date(value || 0);
  if (!value || Number.isNaN(parsed.getTime())) return "—";
  const days = Math.floor((now - parsed) / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days} d ago`;
  return formatDateOnly(value);
}

const timeOf = (value) => {
  const parsed = new Date(value || 0);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

/** The icon tile of a kind, in the kind's colour. */
function KindTile({ kind, size = 36, iconSize = 20 }) {
  const { Icon, accent } = ITEM_KIND_STYLES[kind];
  return (
    <Box
      sx={{
        width: size, height: size, borderRadius: 2, flexShrink: 0, bgcolor: accent, color: "#ffffff",
        display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 6px 14px ${accent}55`,
      }}
      aria-hidden="true"
    >
      <Icon sx={{ fontSize: iconSize }} />
    </Box>
  );
}

const panelSx = {
  borderRadius: 3, border: "1px solid rgba(15, 23, 42, 0.08)", bgcolor: "#ffffff", boxSizing: "border-box",
};

/** A small rounded tag on a row: kind, type, status (with its dot) or project. */
function Tag({ label, color, dot = false, icon = null }) {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex", alignItems: "center", gap: 0.5, px: 0.85, py: 0.15, borderRadius: 999,
        fontSize: 11.5, fontWeight: 600, lineHeight: 1.6, whiteSpace: "nowrap",
        color: color || "text.secondary", bgcolor: color ? `${color}14` : "rgba(15, 23, 42, 0.05)",
        border: `1px solid ${color ? `${color}40` : "rgba(15, 23, 42, 0.08)"}`,
      }}
    >
      {dot ? <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: color }} /> : null}
      {icon}
      {label}
    </Box>
  );
}

/** A panel with a navy header band (icon tile, title, subtitle, an action
 *  on the right) and a white body — the same chrome as the item cards. */
function Panel({ icon, title, subtitle, action, children }) {
  return (
    <Paper elevation={0} sx={{ ...panelSx, overflow: "hidden", display: "flex", flexDirection: "column" }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{ px: 2.5, py: 1.75, background: NAVY_GRADIENT, color: "#ffffff" }}
      >
        <Box
          sx={{
            width: 38, height: 38, borderRadius: 2, flexShrink: 0, display: "flex", alignItems: "center",
            justifyContent: "center", bgcolor: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.12)",
          }}
          aria-hidden="true"
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.2 }}>{title}</Typography>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>{subtitle}</Typography>
        </Box>
        {action}
      </Stack>
      <Box sx={{ p: 1.5, flex: 1 }}>{children}</Box>
    </Paper>
  );
}

/** A count or a note at the right of a panel header. */
function HeaderTag({ label }) {
  return (
    <Box
      component="span"
      sx={{
        px: 1, py: 0.25, borderRadius: 999, fontSize: 11.5, fontWeight: 700, whiteSpace: "nowrap",
        color: "rgba(255,255,255,0.85)", bgcolor: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.16)",
      }}
    >
      {label}
    </Box>
  );
}

const headerButtonSx = {
  textTransform: "none", fontWeight: 600, borderRadius: 2, color: "#ffffff", whiteSpace: "nowrap",
  borderColor: "rgba(255,255,255,0.3)", "&:hover": { borderColor: "#ffffff", bgcolor: "rgba(255,255,255,0.08)" },
};

/** One clickable row of a panel list: a leading tile, two lines, trailing text and a chevron. */
function PanelRow({ onOpen, tile, primary, secondary, trailing }) {
  return (
    <Box
      component="li"
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") onOpen(); }}
      sx={{
        display: "flex", alignItems: "center", gap: 1.5, py: 1.1, px: 1.25, borderRadius: 2.5, cursor: "pointer",
        border: "1px solid transparent", transition: "background-color 0.15s ease, border-color 0.15s ease",
        "& .row-chevron": { opacity: 0, transition: "opacity 0.15s ease, transform 0.15s ease" },
        "&:hover, &:focus-visible": {
          bgcolor: "rgba(76, 126, 255, 0.06)", borderColor: "rgba(76, 126, 255, 0.2)", outline: "none",
          "& .row-chevron": { opacity: 1, transform: "translateX(2px)" },
        },
      }}
    >
      {tile}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        {primary}
        <Stack direction="row" spacing={0.6} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
          {secondary}
        </Stack>
      </Box>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
        {trailing}
        <ChevronRightIcon className="row-chevron" sx={{ fontSize: 18, color: "#4C7EFF" }} />
      </Stack>
    </Box>
  );
}

/** The empty state of a panel: an icon, a line, an optional action. */
function PanelEmpty({ icon, text, action }) {
  return (
    <Stack alignItems="center" spacing={1} sx={{ py: 3.5, textAlign: "center" }}>
      <Box sx={{ color: "rgba(15, 23, 42, 0.25)" }} aria-hidden="true">{icon}</Box>
      <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 320 }}>{text}</Typography>
      {action}
    </Stack>
  );
}

const rowNameSx = { fontWeight: 700, fontSize: 13.5, lineHeight: 1.3 };

function Home() {
  const navigate = useNavigate();
  const signedIn = isSignedIn();
  const [profile, setProfile] = useState(null);
  const [projects, setProjects] = useState(null); // { projects, unfiled } once loaded
  const [recent, setRecent] = useState([]);
  const [accessibleModules, setAccessibleModules] = useState(null); // Set once known

  useEffect(() => {
    if (!signedIn) return undefined;
    let cancelled = false;
    const keep = (setter) => (value) => { if (!cancelled) setter(value); };
    const swallow = (label) => (error) => { console.error(`${label} failed:`, error); };
    Promise.resolve().then(() => axios.get("api/user/profile/"))
      .then((res) => keep(setProfile)(res?.data || null)).catch(swallow("Loading the profile"));
    Promise.resolve().then(() => fetchProjects())
      .then(keep(setProjects)).catch(swallow("Loading the projects"));
    const userId = localStorage.getItem("userId");
    if (userId && userId !== "null" && userId !== "undefined") {
      Promise.resolve().then(() => fetchModuleAccess(userId))
        .then((modules) => { if (Array.isArray(modules)) keep(setAccessibleModules)(new Set(modules)); })
        .catch(swallow("Loading the module access"));
    }
    const lists = [
      ["exploration", `api/exploration/data?page=0&per_page=${RECENT_PER_KIND}&sort_by=date&sort_order=desc`,
        (data) => data?.data || []],
      ["selection", `api/selection/data?page=0&per_page=${RECENT_PER_KIND}`, (data) => data?.data || []],
      ["reproduction", "api/reproduction/get_existing_reproductions", (data) => (Array.isArray(data) ? data : [])],
    ];
    Promise.allSettled(lists.map(([kind, url, rowsOf]) =>
      Promise.resolve().then(() => axios.get(url)).then((res) => rowsOf(res?.data).map((row) => recentOf(kind, row)))
    )).then((results) => {
      if (cancelled) return;
      const merged = results.flatMap((result) => (result.status === "fulfilled" ? result.value : []));
      merged.sort((a, b) => timeOf(b.date) - timeOf(a.date));
      setRecent(merged.slice(0, RECENT_SHOWN));
    });
    return () => { cancelled = true; };
  }, [signedIn]);

  const restricted = (moduleId) => accessibleModules !== null && !accessibleModules.has(moduleId);

  const totals = useMemo(() => {
    if (!projects) return null;
    const sum = (field) => projects.projects.reduce((acc, p) => acc + Number(p[field] || 0), 0) + Number(projects.unfiled[field] || 0);
    return {
      projects: projects.projects.length,
      exploration: sum("exploration_count"),
      selection: sum("selection_count"),
      reproduction: sum("reproduction_count"),
    };
  }, [projects]);

  const topProjects = useMemo(() => {
    if (!projects) return [];
    return [...projects.projects]
      .sort((a, b) => countsOf(b).total - countsOf(a).total || a.name.localeCompare(b.name))
      .slice(0, PROJECTS_SHOWN);
  }, [projects]);

  const name = signedIn ? displayName(profile) : "";
  const today = new Date();

  return (
    <Stack direction="column" alignItems="center" spacing={4} marginTop="2%">
      <Box sx={{ width: "100%", maxWidth: "1150px", px: 2, boxSizing: "border-box" }}>
        {/* hero */}
        <Box
          sx={{
            borderRadius: 3, color: "#ffffff", p: { xs: 2.5, sm: 3.5 }, background: NAVY_GRADIENT,
            border: "1px solid rgba(255,255,255,0.06)", boxShadow: "0 18px 40px rgba(15, 23, 42, 0.22)",
          }}
        >
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2.5} alignItems={{ xs: "flex-start", md: "center" }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="overline" sx={{ color: "rgba(255,255,255,0.5)", fontWeight: 700, letterSpacing: 1.2 }}>
                {today.toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.15, mt: 0.5 }}>
                {signedIn ? `${greetingFor(today)}${name ? `, ${name}` : ""}` : "Welcome to Oraclase"}
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.72)", mt: 1, maxWidth: 560 }}>
                Explore the colours a laser can mark, select a palette, reproduce a design. Projects keep the work of one job together.
              </Typography>
            </Box>
            {signedIn ? (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ flexShrink: 0 }}>
                {QUICK_ACTIONS.map((action) => (
                  <Button
                    key={action.kind}
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    disabled={restricted(action.moduleId)}
                    onClick={() => navigate(action.to)}
                    sx={{
                      ...headerButtonSx,
                      "&.Mui-disabled": { color: "rgba(255,255,255,0.35)", borderColor: "rgba(255,255,255,0.15)" },
                    }}
                  >
                    {action.label}
                  </Button>
                ))}
                <Button
                  size="small"
                  variant="contained"
                  disableElevation
                  startIcon={<FolderOutlinedIcon />}
                  onClick={() => navigate(PROJECTS_ROUTE)}
                  sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2, bgcolor: "#4C7EFF", "&:hover": { bgcolor: "#3b6ae6" } }}
                >
                  Projects
                </Button>
              </Stack>
            ) : (
              <Button
                variant="contained"
                disableElevation
                startIcon={<LoginIcon />}
                onClick={() => navigate("/login")}
                sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2, bgcolor: "#4C7EFF", "&:hover": { bgcolor: "#3b6ae6" }, flexShrink: 0 }}
              >
                Sign in
              </Button>
            )}
          </Stack>
        </Box>

        {/* headline numbers */}
        {signedIn ? (
          <Box
            sx={{
              display: "grid", gap: 2, mt: 3,
              gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" },
            }}
          >
            {[
              { label: "Projects", value: totals?.projects, to: PROJECTS_ROUTE, tile: (
                <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: PROJECT_TILE_COLOR, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 6px 14px ${PROJECT_TILE_COLOR}55` }} aria-hidden="true"><FolderOutlinedIcon sx={{ fontSize: 20 }} /></Box>
              ) },
              { label: "Explorations", value: totals?.exploration, to: EXPLORATION_LIST_ROUTE, tile: <KindTile kind="exploration" /> },
              { label: "Selections", value: totals?.selection, to: SELECTIONS_BASE, tile: <KindTile kind="selection" /> },
              { label: "Reproductions", value: totals?.reproduction, to: REPRODUCTIONS_BASE, tile: <KindTile kind="reproduction" /> },
            ].map((stat) => (
              <Paper
                key={stat.label}
                elevation={0}
                role="button"
                tabIndex={0}
                aria-label={`${stat.label}: ${stat.value ?? "…"}`}
                onClick={() => navigate(stat.to)}
                onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") navigate(stat.to); }}
                sx={{
                  ...panelSx, p: 2, display: "flex", alignItems: "center", gap: 1.5, cursor: "pointer",
                  transition: "box-shadow 0.2s ease, transform 0.2s ease",
                  "&:hover": { boxShadow: "0 10px 30px rgba(15, 23, 42, 0.08)", transform: "translateY(-2px)" },
                }}
              >
                {stat.tile}
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 24, fontWeight: 800, lineHeight: 1.1 }}>
                    {stat.value ?? "…"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>{stat.label}</Typography>
                </Box>
              </Paper>
            ))}
          </Box>
        ) : null}

        {/* recent + projects */}
        {signedIn ? (
          <Box sx={{ display: "grid", gap: 2.5, mt: 3, mb: 4, gridTemplateColumns: { xs: "1fr", md: "minmax(0, 3fr) minmax(0, 2fr)" } }}>
            <Panel
              icon={<HistoryIcon sx={{ fontSize: 20 }} />}
              title="Recent activity"
              subtitle="The latest changes across the team"
              action={recent.length > 0 ? <HeaderTag label={`${recent.length} latest`} /> : null}
            >
              {recent.length === 0 ? (
                <PanelEmpty
                  icon={<HistoryIcon sx={{ fontSize: 36 }} />}
                  text="Nothing yet. The explorations, selections and reproductions the team works on will show up here."
                  action={(
                    <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => navigate(EXPLORATION_SETUP_ROUTE)} sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}>
                      New exploration
                    </Button>
                  )}
                />
              ) : (
                <Stack component="ul" aria-label="recent activity" spacing={0.25} sx={{ listStyle: "none", m: 0, p: 0 }}>
                  {recent.map((item) => {
                    const { label, accent } = ITEM_KIND_STYLES[item.kind];
                    return (
                      <PanelRow
                        key={item.key}
                        onOpen={() => navigate(item.to)}
                        tile={<KindTile kind={item.kind} size={34} iconSize={18} />}
                        primary={<Typography noWrap title={item.name} sx={rowNameSx}>{item.name}</Typography>}
                        secondary={(
                          <>
                            <Tag label={label.replace(/s$/, "")} color={accent} />
                            {item.type ? <Tag label={item.type} /> : null}
                            <Tag label={item.status} color={item.statusColor} dot />
                            {item.project ? <Tag label={item.project} icon={<FolderOutlinedIcon sx={{ fontSize: 12 }} />} /> : null}
                          </>
                        )}
                        trailing={(
                          <Tooltip title={formatDateTime(item.date)}>
                            <Typography variant="caption" sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
                              {relativeDate(item.date)}
                            </Typography>
                          </Tooltip>
                        )}
                      />
                    );
                  })}
                </Stack>
              )}
            </Panel>

            <Panel
              icon={<FolderOutlinedIcon sx={{ fontSize: 20 }} />}
              title="Projects"
              subtitle="Where the team's work is filed"
              action={(
                <Button size="small" variant="outlined" endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />} onClick={() => navigate(PROJECTS_ROUTE)} sx={headerButtonSx}>
                  All projects
                </Button>
              )}
            >
              {projects && topProjects.length === 0 ? (
                <PanelEmpty
                  icon={<CreateNewFolderOutlinedIcon sx={{ fontSize: 36 }} />}
                  text="No projects yet. A project keeps the explorations, selections and reproductions of one job together."
                  action={(
                    <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => navigate(PROJECTS_ROUTE)} sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}>
                      New project
                    </Button>
                  )}
                />
              ) : (
                <Stack component="ul" aria-label="projects" spacing={0.25} sx={{ listStyle: "none", m: 0, p: 0 }}>
                  {topProjects.map((project) => {
                    const tile = projectTile(project.name);
                    const counts = countsOf(project);
                    const parts = [
                      ["exploration", counts.explorations], ["selection", counts.selections], ["reproduction", counts.reproductions],
                    ];
                    return (
                      <PanelRow
                        key={project.id}
                        onOpen={() => navigate(projectRoute(project.id))}
                        tile={(
                          <Box
                            sx={{
                              width: 34, height: 34, borderRadius: 2, flexShrink: 0, bgcolor: tile.color, color: "#fff",
                              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800,
                              boxShadow: `0 6px 14px ${tile.color}55`,
                            }}
                            aria-hidden="true"
                          >
                            {tile.initials}
                          </Box>
                        )}
                        primary={<Typography noWrap title={project.name} sx={rowNameSx}>{project.name}</Typography>}
                        secondary={(
                          <>
                            {parts.map(([kind, count]) => {
                              const { Icon, accent } = ITEM_KIND_STYLES[kind];
                              return (
                                <Box key={kind} component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.4, color: count > 0 ? accent : "rgba(15, 23, 42, 0.3)", fontSize: 12, fontWeight: 700 }}>
                                  <Icon sx={{ fontSize: 13 }} />{count}
                                </Box>
                              );
                            })}
                            {/* composition of the project along the workflow */}
                            <Box component="span" aria-label={counts.long} sx={{ display: "flex", gap: "2px", height: 5, width: 72, borderRadius: 999, overflow: "hidden", bgcolor: "rgba(15, 23, 42, 0.06)", ml: 0.5 }}>
                              {counts.total > 0 ? parts.filter(([, count]) => count > 0).map(([kind, count]) => (
                                <Box key={kind} component="span" sx={{ flex: count, bgcolor: ITEM_KIND_STYLES[kind].accent }} />
                              )) : null}
                            </Box>
                          </>
                        )}
                        trailing={(
                          <Typography variant="caption" sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
                            {counts.total} item{counts.total === 1 ? "" : "s"}
                          </Typography>
                        )}
                      />
                    );
                  })}
                </Stack>
              )}
            </Panel>
          </Box>
        ) : <Box sx={{ mb: 4 }} />}
      </Box>
    </Stack>
  );
}

export default Home;
